import { useMutation } from '@apollo/client';
import type { RentalApplication } from '../graphql/queries';
import {
  ApplicantStatus,
  ApplicationStatus,
  GetApplicationsForUserDocument,
  WithdrawApplicationDocument,
  WithdrawSharedApplicationDocument,
} from '../graphql/queries';

export function useWithdrawApplication(hasListing: boolean) {
  const [mutate, { loading }] = useMutation(
    hasListing ? WithdrawApplicationDocument : WithdrawSharedApplicationDocument,
    {
      refetchQueries: [GetApplicationsForUserDocument],
    },
  );

  const withdrawApplication = async (options: Parameters<typeof mutate>[0]) => {
    const applicationId = options?.variables?.applicationId;
    if (!applicationId) {
      throw new Error('Missing applicationId');
    }

    return await mutate({
      ...options,
      // Updates the cache manually to move the withdrawn application to the archived list
      // this is needed because the GetApplicationsForUserDocument is not used in the application
      // page, so the cache is not updated automatically by using refetchQueries outside the applications page
      update: (cache) => {
        cache.modify<RentalApplication>({
          id: cache.identify({
            __typename: 'RentalApplication',
            id: applicationId,
          } satisfies Pick<RentalApplication, '__typename' | 'id'>),
          fields: {
            isArchived: () => true,
            status: () => ApplicantStatus.withdrawn,
            applicationStatus: () => ApplicationStatus.withdrawn,
          },
        });
      },
    });
  };

  return [withdrawApplication, { loading }] as const;
}
